import { arrayToMap } from '@sparted/shared-library';
import { type LanguageCode } from '@sparted/shared-library/business/types';
import { t } from 'i18next';
import React, { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import MModal from 'Components/modal/MModal/MModal';
import SActionBar, { OptionType } from 'Components/structural/SActionBar/SActionBar';
import { useTypedSelector } from 'Libs/redux/utils';
import { redirect } from 'Libs/routing';
import { enqueueBasicAlert } from 'Pages/AlertManager';
import useArchiveAction from 'Pages/Activity/hooks/useArchiveAction';
import { getCurrentBOLanguageOrDefault, getLanguagesAvailableWithCode } from 'Services/language/languageService';
import { duplicateBreakingNews } from 'Store/entities/activity/activity.api';
import { BreakingNews } from 'Store/entities/activity/activity.type';

import { BreakingNewsFormValues } from '../../BreakingNewsForm.hook';
import { useCheckRequiredFields } from '../../CheckRequiredFields.hook';
import { redirectToActivityList } from '../../routing.utils';
import LanguagePanel from '../LanguagePanel/LanguagePanel';
import style from './ActionBar.style';

const STATUS_MAP = {
  draft: 'inactive',
  inactive: 'inactive',
  live: 'active',
  upcoming: 'active',
  finished: 'active',
  archived: 'inactive',
} as const;

const getLanguageOptions = (
  breakingNewsId: number,
  languages: LanguageCode[],
  selectedLanguageCode: LanguageCode,
  completedLanguages: LanguageCode[],
  onLanguageChange: (language: LanguageCode) => void,
) => {
  const availableLanguages = getLanguagesAvailableWithCode();
  const availableLanguagesByCode = arrayToMap(availableLanguages, {
    selectKey: (lang) => lang.languageCode,
  });

  return languages
    .map((languageCode) => {
      const language = availableLanguagesByCode[languageCode];

      return {
        type: 'language',
        id: language.id,
        key: `${breakingNewsId}-${language.id}`,
        props: {
          isDefault: language.isDefault,
          defaultLabel: t('breaking_news:language_menu.default'),
          switchToLabel: t('breaking_news:language_menu.switch_to'),
          validLabel: t('breaking_news:language_menu.ready'),
          text: language.languageName,
          countryCode: language.countryCode,
          selected: selectedLanguageCode === languageCode,
          onClick: () => onLanguageChange(languageCode),
          status: completedLanguages.includes(languageCode) ? 'valid' : 'warning',
          statusText: completedLanguages.includes(languageCode)
            ? t('breaking_news:language_menu.ready')
            : t('breaking_news:language_menu.not_ready'),
        },
      } as const;
    })
    .sort((a, b) => (a.props.isDefault ? -1 : b.props.isDefault ? 1 : 0));
};

export type ActionBarProps = {
  tabs: string[];
  currentTabIndex: number;
  breakingNewsId: number;
  breakingNewsName: string;
  breakingNewsStatus: BreakingNews['status'];
  breakingNewsLastSave?: string;
  breakingNewsLanguages: LanguageCode[];
  selectedLanguage: LanguageCode;
  disableSave?: boolean;
  saving?: boolean;
  onTabChange: (index: number) => void;
  onLanguageChange: (language: LanguageCode) => void;
  onLiveToggle: (isActive: boolean) => void;
  onSave: () => Promise<void>;
};

const ALERT_DURATION = 5000;

export const ActionBar: React.FC<ActionBarProps> = ({
  tabs,
  currentTabIndex,
  breakingNewsId,
  breakingNewsName,
  breakingNewsStatus,
  breakingNewsLastSave,
  breakingNewsLanguages,
  selectedLanguage,
  disableSave,
  saving,
  onTabChange,
  onLanguageChange,
  onLiveToggle,
  onSave,
}) => {
  const {
    watch,
    formState: { isDirty },
  } = useFormContext<BreakingNewsFormValues>();

  const { handleArchiveClick, archiveWarningModal } = useArchiveAction();

  const isArchived = breakingNewsStatus === 'archived';

  const { breakingNews } = useTypedSelector((state) => state.entities.activity.edition);

  if (!breakingNews) return null;

  const isActive = useMemo(() => ['live', 'upcoming', 'finished'].includes(breakingNewsStatus), [breakingNewsStatus]);

  const { missingCurrentFields, completedLanguages } = useCheckRequiredFields({
    watch,
    breakingNewsLanguages,
  });

  const [isLanguagePanelVisible, setLanguagePanelVisible] = useState(false);
  const [modalExitWithoutSaving, setModalmodalExitWithoutSaving] = useState(false);

  const handleDuplicate = useCallback(async () => {
    const { languageCode } = getCurrentBOLanguageOrDefault();

    try {
      const duplicatedBreakingNews = await duplicateBreakingNews(breakingNewsId, languageCode);
      redirect(`/activity/breaking-news/${duplicatedBreakingNews.id}`);
      enqueueBasicAlert(
        {
          id: 'bn_duplication_failed',
          status: 'success',
          title: t('activities:alerts.success.duplicate'),
        },
        ALERT_DURATION,
      );
    } catch (error) {
      enqueueBasicAlert(
        {
          id: 'bn_duplication_failed',
          status: 'error',
          title: t('activities:alerts.errors.duplicate_error_message'),
        },
        ALERT_DURATION,
      );
    }
  }, []);

  const options: OptionType[] = useMemo(
    () => [
      {
        title: t('activities:actions.duplicate'),
        callback: handleDuplicate,
      },
      {
        title: isArchived ? t('activities:actions.unarchive') : t('activities:actions.archive'),
        callback: () => handleArchiveClick(breakingNewsId, isArchived),
      },
    ],
    [isArchived, breakingNewsId, handleArchiveClick, handleDuplicate],
  );

  const languageOptions = useMemo(
    () =>
      [
        ...getLanguageOptions(
          breakingNewsId,
          breakingNewsLanguages,
          selectedLanguage,
          completedLanguages,
          onLanguageChange,
        ),
        {
          type: 'divider',
          key: 'divider-0',
          props: {
            style: {
              height: 0,
            },
          },
        },
        {
          type: 'standard',
          key: 'text-change',
          textPosition: 'center',
          text: t('activities:actions.manage_languages'),
          onClick: () => setLanguagePanelVisible(true),
        },
      ] as const,
    [breakingNewsId, breakingNewsLanguages, selectedLanguage, completedLanguages, onLanguageChange],
  );

  const generateErrorMessage = useCallback(
    (missingFields: { [key in LanguageCode]?: string[] }) => {
      const allMissingFields = new Set(Object.values(missingFields).flatMap((fields) => fields));

      return (
        <>
          <span>{t('breaking_news:edition.update_fail_prefix', { count: allMissingFields.size })}</span>
          <ul style={style.errorMessageList}>
            {Array.from(allMissingFields).map((field) => (
              <li key={field}>{t(`breaking_news:edition.${field}`)}</li>
            ))}
          </ul>
        </>
      );
    },
    [t],
  );

  const handleSave = useCallback(async () => {
    if (isActive && Object.keys(missingCurrentFields).length) {
      const errorMessage = generateErrorMessage(missingCurrentFields);

      enqueueBasicAlert(
        {
          id: 'breaking_news_edition_failed',
          priority: 'medium',
          status: 'error',
          title: t('breaking_news:edition.update_active_fail'),
        },
        ALERT_DURATION,
        errorMessage,
      );

      return;
    }
    await onSave();
  }, [onSave]);

  const handleLive = useCallback(
    async (isActive: boolean) => {
      if (isActive && Object.keys(missingCurrentFields).length) {
        const errorMessage = generateErrorMessage(missingCurrentFields);

        enqueueBasicAlert(
          {
            id: 'breaking_news_edition_failed',
            priority: 'medium',
            status: 'error',
            title: t('breaking_news:edition.update_active_fail'),
          },
          ALERT_DURATION,
          errorMessage,
        );

        return;
      }
      onLiveToggle(isActive);
    },
    [onLiveToggle],
  );

  return (
    <>
      <SActionBar
        id={breakingNewsId}
        tabs={tabs}
        title={breakingNewsName}
        titlePlaceholder={t('breaking_news:placeholder.title')}
        isActive={isActive}
        selectedTab={currentTabIndex}
        onChangeTab={onTabChange}
        onSave={handleSave}
        onBack={() => {
          isDirty ? setModalmodalExitWithoutSaving(true) : redirectToActivityList();
        }}
        onToggleChange={handleLive}
        options={options}
        status={STATUS_MAP[breakingNewsStatus]}
        statusWording={t(`activities:status.${STATUS_MAP[breakingNewsStatus]}`)}
        inactiveToggleLabel={t('activities:status.inactive')}
        canActivate={!Object.keys(missingCurrentFields).length || isActive}
        tooltipText={t('breaking_news:edition.toogle_tooltip')}
        lastSaved={breakingNewsLastSave}
        optionsLanguages={languageOptions}
        canSave={!disableSave}
        canLock={false}
        isSaveLoading={saving}
        onLock={() => {}}
        dotMenu={true}
        backArrow={true}
        useConditionalStyles={true}
      />
      <LanguagePanel
        visible={isLanguagePanelVisible}
        missingMandatoryFields={missingCurrentFields}
        isBreakingNewsLive={isActive}
        onClose={() => setLanguagePanelVisible(false)}
      />
      {/* Archive warning modal */}
      {archiveWarningModal}

      {/* Unsaved changes warning modal */}
      <MModal
        onCloseModal={() => setModalmodalExitWithoutSaving(false)}
        visible={modalExitWithoutSaving}
        type="dialog"
        title={t('breaking_news:edition.modal.unsaved_changes.title')}
        labelActionButton={t('breaking_news:edition.modal.unsaved_changes.save_and_exit')}
        labelSecondButton={t('breaking_news:edition.modal.unsaved_changes.exit_without_saving')}
        onAction={handleSave}
        onActionEnd={redirectToActivityList}
        isActionLoading={saving}
        onSecondAction={redirectToActivityList}
        showExitCross
      >
        {t('breaking_news:edition.modal.unsaved_changes.text')}
      </MModal>
    </>
  );
};
